import { action, computed, makeObservable, observable } from 'mobx';
import type { RootStore } from 'logic';
import { STEP_TAB_DONE, STEP_TAB_TRIAGE } from 'logic/ui-active-session';
import { matchesAnyNormalizedSearchTerm, splitNormalizedSearchTerms } from 'utils/textSearch';
import { getStaticStepDisplay } from './stepDisplayUtils';

export interface SearchResultCounts {
  total: number;
  matches: number;
  nameMatch: boolean;
  contentMatches: number;
}

export interface StepSearchCounts {
  triage: SearchResultCounts;
  done: SearchResultCounts;
  steps: Record<string, SearchResultCounts>;
}

export interface LibrarySourceSearchCounts {
  total: number;
  matches: number;
  contentMatches: number;
  cardMatchedCount: number;
  nameMatch: boolean;
}

export class UiProjectSearchStore {
  private rootStore: RootStore;
  searchTerm: string = '';
  lastSearchTime: number = 0;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this, {
      searchTerm: observable,
      lastSearchTime: observable,
      setSearchTerm: action.bound,
      clearSearch: action.bound,
      clearSearchIfExpired: action.bound,
      hasSearch: computed,
      searchTerms: computed,
      stepSearchCounts: computed,
    });
  }

  setSearchTerm(term: string) {
    this.searchTerm = term;
    this.lastSearchTime = Date.now();
  }

  clearSearch() {
    this.searchTerm = '';
    this.lastSearchTime = 0;
  }

  clearSearchIfExpired() {
    const now = Date.now();
    const thirtySeconds = 30 * 1000;
    if (this.lastSearchTime > 0 && now - this.lastSearchTime > thirtySeconds) {
      this.clearSearch();
    }
  }

  get hasSearch() {
    return !!this.searchTerm?.trim();
  }

  get searchTerms() {
    return splitNormalizedSearchTerms(this.searchTerm);
  }

  // Compute matching rows for module fields (similar to uiCardSearchStore)
  private getSearchMatchingRowsByModuleId(): Record<string, Set<string>> {
    const { sessionsStore, sourcesStore } = this.rootStore;
    const { currentSession } = sessionsStore;

    if (!this.hasSearch || !currentSession) {
      return {};
    }

    const matchingRowsByModuleId: Record<string, Set<string>> = {};
    const searchTermsLower = this.searchTerms;

    // Search through each module in the session
    currentSession.modules.forEach((module) => {
      const moduleSource = sourcesStore.sources[module.sourceId];
      if (!moduleSource) return;

      // Search all fields for this module
      const activeFields = moduleSource.ownFields.map((f) => f.fieldId);
      if (activeFields.length === 0) return;

      const matchingRows = new Set<string>();

      // Search through all rows in this source
      Object.values(moduleSource.rows).forEach((row) => {
        const rowMatches = searchTermsLower.some((searchTerm) =>
          Object.entries(row.getFieldValues()).some(([fieldId, value]) => {
            return (
              activeFields.includes(fieldId) &&
              typeof value === 'string' &&
              value.length > 0 &&
              matchesAnyNormalizedSearchTerm(value, [searchTerm])
            );
          }),
        );

        if (rowMatches) {
          matchingRows.add(row.id);
        }
      });

      if (matchingRows.size > 0) {
        matchingRowsByModuleId[module.id] = matchingRows;
      }
    });

    return matchingRowsByModuleId;
  }

  // Get queue card counts with search filtering
  get queueCardCounts() {
    const { sessionsStore, cardsStore, deletedCardsStore } = this.rootStore;
    const { currentSession } = sessionsStore;

    if (!currentSession) {
      return { active: 0, done: 0, deleted: 0 };
    }

    const { inProgressCardIds, doneCardIds } = currentSession;
    const allCardIds = currentSession.data.cardIds ?? [];

    if (!this.hasSearch) {
      // Count without search filter
      const deletedCount = allCardIds.filter((id) => !!deletedCardsStore.deletedCards[id]).length;

      return {
        active: inProgressCardIds.length,
        done: doneCardIds.length,
        deleted: deletedCount,
      };
    }

    // Apply search filter - compute our own module matches
    const searchTermsLower = this.searchTerms;
    const searchMatchingRowsByModuleId = this.getSearchMatchingRowsByModuleId();

    const triageLabel = getStaticStepDisplay(STEP_TAB_TRIAGE)?.label ?? 'Pending';
    const doneLabel = getStaticStepDisplay(STEP_TAB_DONE)?.label ?? 'Done';

    // Check if search terms match queue tab names
    const activeNameMatch = matchesAnyNormalizedSearchTerm(triageLabel, searchTermsLower);
    const doneNameMatch = matchesAnyNormalizedSearchTerm(doneLabel, searchTermsLower);
    const deletedNameMatch = matchesAnyNormalizedSearchTerm('deleted', searchTermsLower);

    const matchCard = (card: any) => {
      if (!card) return false;

      // Check card title
      const titleMatch = matchesAnyNormalizedSearchTerm(card.mainLabel, searchTermsLower);
      if (titleMatch) return true;

      // Check filename
      const filenameMatch = matchesAnyNormalizedSearchTerm(card.filename, searchTermsLower);
      if (filenameMatch) return true;

      // Check module field values
      for (const [moduleId, searchMatchingRows] of Object.entries(searchMatchingRowsByModuleId)) {
        const connectedRows = card.getConnectedRows(moduleId);
        if (connectedRows.some((rId: string) => searchMatchingRows.has(rId))) {
          return true;
        }
      }

      return false;
    };

    // Count matching active cards (show all if tab name matches, otherwise filter by content)
    const activeMatches = activeNameMatch
      ? inProgressCardIds.length
      : inProgressCardIds.filter((id) => {
          const card = cardsStore.cards[id];
          return matchCard(card);
        }).length;

    // Count matching done cards (show all if tab name matches, otherwise filter by content)
    const doneMatches = doneNameMatch
      ? doneCardIds.length
      : doneCardIds.filter((id) => {
          const card = cardsStore.cards[id];
          return matchCard(card);
        }).length;

    // Count matching deleted cards (show all if tab name matches, otherwise filter by content)
    const deletedCount = allCardIds.filter((id) => !!deletedCardsStore.deletedCards[id]).length;
    const deletedMatches = deletedNameMatch
      ? deletedCount
      : allCardIds.filter((id) => {
          const card = deletedCardsStore.deletedCards[id];
          return card && matchCard(card);
        }).length;

    return {
      active: activeMatches,
      done: doneMatches,
      deleted: deletedMatches,
    };
  }

  get stepSearchCounts(): StepSearchCounts {
    const { sessionsStore, cardsStore } = this.rootStore;
    const { currentSession } = sessionsStore;

    if (!currentSession) {
      return {
        triage: { total: 0, matches: 0, nameMatch: false, contentMatches: 0 },
        done: { total: 0, matches: 0, nameMatch: false, contentMatches: 0 },
        steps: {},
      };
    }

    const searchTermsLower = this.searchTerms;
    const searchMatchingRowsByModuleId = this.getSearchMatchingRowsByModuleId();
    const triageLabel = getStaticStepDisplay(STEP_TAB_TRIAGE)?.label ?? 'Pending';
    const doneLabel = getStaticStepDisplay(STEP_TAB_DONE)?.label ?? 'Done';

    const matchCard = (card: any) => {
      if (!card) return false;

      const titleMatch = matchesAnyNormalizedSearchTerm(card.mainLabel, searchTermsLower);
      if (titleMatch) return true;

      const filenameMatch = matchesAnyNormalizedSearchTerm(card.filename, searchTermsLower);
      if (filenameMatch) return true;

      for (const [moduleId, searchMatchingRows] of Object.entries(searchMatchingRowsByModuleId)) {
        const connectedRows = card.getConnectedRows(moduleId);
        if (connectedRows.some((rId: string) => searchMatchingRows.has(rId))) {
          return true;
        }
      }

      return false;
    };

    const getCountsForBucket = (cardIds: string[], bucketLabel: string): SearchResultCounts => {
      const total = cardIds.length;
      if (!this.hasSearch) {
        return { total, matches: total, nameMatch: false, contentMatches: total };
      }

      const labelMatch = matchesAnyNormalizedSearchTerm(bucketLabel, searchTermsLower);
      const contentMatches = cardIds.filter((id) => {
        const card = cardsStore.cards[id];
        return matchCard(card);
      }).length;

      return {
        total,
        matches: labelMatch ? total : contentMatches,
        nameMatch: labelMatch,
        contentMatches,
      };
    };

    const steps: Record<string, SearchResultCounts> = {};
    currentSession.sortedSteps.forEach((step) => {
      steps[step.id] = getCountsForBucket(currentSession.getCardIdsByStepId(step.id), step.name);
    });

    return {
      triage: getCountsForBucket(currentSession.getCardIdsByStepId(null), triageLabel),
      done: getCountsForBucket(currentSession.doneCardIds, doneLabel),
      steps,
    };
  }

  // Get library source match counts
  get librarySourceMatches() {
    const { sessionsStore, sourcesStore, uiModulePowerColumnStore, cardsStore } = this.rootStore;
    const { currentSession } = sessionsStore;

    if (!currentSession || !currentSession.data) {
      return {};
    }

    const sourceIds = [...new Set(currentSession.data.sourceIds)];
    const matches: Record<string, LibrarySourceSearchCounts> = {};

    sourceIds.forEach((sourceId) => {
      const source = sourcesStore.sources[sourceId];
      if (!source) return;

      const totalRows = source.orderedRows.length;

      if (this.hasSearch) {
        // Calculate match count directly without modifying other store state
        const searchTermsLower = this.searchTerms;

        // Check if search terms match the source name
        const sourceName = source.data.name || '';
        const sourceNameMatch = matchesAnyNormalizedSearchTerm(sourceName, searchTermsLower);

        // Search through row field values even when the source name matches so click-through
        // can distinguish name-only matches from mixed name+content matches.
        const activeFields = source.ownFields.map((f) => f.fieldId);
        const powerColumn = uiModulePowerColumnStore.getSourceModulePowerColumn(sourceId);

        let contentMatches = 0;
        let cardMatchedCount = 0;
        if (activeFields.length > 0) {
          source.orderedRows.forEach((row) => {
            let rowMatches = searchTermsLower.some((searchTerm) =>
              Object.entries(row.getFieldValues()).some(([fieldId, value]) => {
                return (
                  activeFields.includes(fieldId) &&
                  typeof value === 'string' &&
                  value.length > 0 &&
                  matchesAnyNormalizedSearchTerm(value, [searchTerm])
                );
              }),
            );

            let cardMatch = false;
            // Search by card connections if enabled and no direct match yet
            // This matches Source.getLibrarySearchResults() logic
            if (!rowMatches && source.searchByCardConnections && powerColumn?.selectedModule) {
              const module = powerColumn.selectedModule;

              // Find all cards connected to this row via the current module
              const connectedCards = Object.values(cardsStore.cards).filter((card) => {
                const connectedRowIds = card.getConnectedRows(module.id);
                return connectedRowIds.includes(row.id);
              });

              // Search through all module connections and field values of connected cards
              for (const card of connectedCards) {
                // Check all modules in the session
                for (const sessionModule of currentSession.modules) {
                  const moduleSource = sourcesStore.sources[sessionModule.sourceId];
                  if (!moduleSource) continue;

                  // Get connected rows for this module
                  const connectedRowIds = card.getConnectedRows(sessionModule.id);

                  // Search through field values of all connected rows
                  for (const connectedRowId of connectedRowIds) {
                    const connectedRow = moduleSource.rows[connectedRowId];
                    if (!connectedRow) continue;

                    // Search through all field values
                    for (const fieldId of moduleSource.data.fieldIds) {
                      const value = connectedRow.getValue(fieldId);
                      if (value && matchesAnyNormalizedSearchTerm(value, searchTermsLower)) {
                        cardMatch = true;
                        break;
                      }
                    }
                    if (cardMatch) break;
                  }
                  if (cardMatch) break;
                }
                if (cardMatch) break;
              }

              // If matched via card connections, mark row as matched and count it
              if (cardMatch) {
                cardMatchedCount++;
                rowMatches = true;
              }
            }

            if (rowMatches) {
              contentMatches++;
            }
          });
        }

        matches[sourceId] = {
          total: totalRows,
          matches: sourceNameMatch ? totalRows : contentMatches,
          contentMatches,
          cardMatchedCount,
          nameMatch: sourceNameMatch,
        };
      } else {
        matches[sourceId] = {
          total: totalRows,
          matches: totalRows,
          contentMatches: totalRows,
          cardMatchedCount: 0,
          nameMatch: false,
        };
      }
    });

    return matches;
  }
}
